import { all, call, fork, put, take, race, select } from "redux-saga/effects";
import { delay } from "redux-saga";

import { MessageRoomPartialID } from "../../domain/model/entity";
import { Message } from "../../domain/model/message";
import { MessageRoom } from "../../domain/model/message_room";
import { Customer } from "../../domain/model/customer";
import { Project } from "../../domain/model/project";
import { IMessageRepository } from "../../domain/repository/message_repository";
import { IMessageRoomRepository } from "../../domain/repository/message_room_repository";
import { ICustomerRepository } from "../../domain/repository/customer_repository";
import { IProjectRepository } from "../../domain/repository/project_repository";

import { createApiClient, IApiClient } from "../../infra/api/client";
import { createMessageAPI } from "../../infra/api/service/message_api";
import { createMessageRoomAPI } from "../../infra/api/service/message_room_api";
import { createCustomerAPI } from "../../infra/api/service/customer_api";
import { createProjectAPI } from "../../infra/api/service/project_api";

import {
  ClientMessageRoomsActionType,
  createClientMessageRoomsActionCreator,
  IClientMessageRoomsActionCreator,
} from "../action/client_message_rooms_action";
import { handleErrorForHandler } from "./handleErrorForHandler";
import { StartPollingMessageRoomsByClientResponse } from "../response/message_room_response";
import { AppState } from "../store/app_state";
import { ClientMessageRoomsState } from "../store/client_message_rooms_state";
import {
  IContractRepository,
  TContractIdWithoutCompany,
} from "src/domain/repository/contract_repository";
import { Contract } from "src/domain/model/contract";
import { createContractAPI } from "src/infra/api/service/contract_api";

const apiClient: IApiClient = createApiClient();
const messageRoomRepository: IMessageRoomRepository =
  createMessageRoomAPI(apiClient);
const messageRepository: IMessageRepository = createMessageAPI(apiClient);
const customerRepository: ICustomerRepository = createCustomerAPI(apiClient);
const projectRepository: IProjectRepository = createProjectAPI(apiClient);
const contractRepository: IContractRepository = createContractAPI(apiClient);
const actionCreator: IClientMessageRoomsActionCreator =
  createClientMessageRoomsActionCreator();

function* pollTask() {
  while (true) {
    try {
      const messageRooms: MessageRoom[] = yield call(getAllMessageRooms);
      const ids: TContractIdWithoutCompany[] = yield call(
        createIdsList,
        messageRooms
      );
      const contracts: Contract[] = yield call(getContractAllNewestByIDs, ids);
      let customerIds: string[];
      let projectIds: string[];
      let customers: Customer[];
      let projects: Project[];
      let messageRoomPartialIds: MessageRoomPartialID[];
      let latestMessages: Message[];
      const clientMessageRoomsSelector = (state: AppState) =>
        state.clientMessageRoomsReducer;
      const _clientMessageRoomsState: ClientMessageRoomsState = yield select(
        clientMessageRoomsSelector
      );
      const _initialMessageRoom = _clientMessageRoomsState.initialMessageRoom;
      [customerIds, projectIds, messageRoomPartialIds] = yield all([
        call(createCustomerIds, messageRooms),
        call(createProjectIds, messageRooms),
        call(createMessageRoomPartialIds, messageRooms),
      ]);
      [customers, projects, latestMessages] = yield all([
        call(getAllByCustomerIds, customerIds),
        call(getAllByProjectIds, projectIds),
        call(getAllNewestByRoomIDs, messageRoomPartialIds),
      ]);
      const firstMessageRoom = messageRooms.length > 0 ? messageRooms[0] : null;
      const initialMessageRoom: MessageRoom | null = _initialMessageRoom
        ? _initialMessageRoom
        : firstMessageRoom;
      const res: StartPollingMessageRoomsByClientResponse =
        new StartPollingMessageRoomsByClientResponse();
      res.messageRooms = messageRooms;
      res.customers = customers;
      res.projects = projects;
      res.latestMessages = latestMessages;
      res.initialMessageRoom = initialMessageRoom;
      res.contracts = contracts;
      yield put(
        actionCreator.callbackStartPollingMessageRoomsAction(true, res)
      );
      yield call(delay, 30000);
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(
        actionCreator.callbackStartPollingMessageRoomsAction(
          false,
          undefined,
          error.toString()
        )
      );
      yield put(actionCreator.requestStopPollingMessageRoomsAction()); // error時に停止
    }
  }
}

function* handlePollTaskWatcherInClientMessageRooms() {
  while (true) {
    yield take(
      ClientMessageRoomsActionType.REQUEST_START_POLLING_MESSAGE_ROOMS
    );
    yield race({
      pollTask: call(pollTask),
      stopTask: take(
        ClientMessageRoomsActionType.REQUEST_STOP_POLLING_MESSAGE_ROOMS
      ),
    });
  }
}

const createCustomerIds = (messageRooms: MessageRoom[]): string[] => {
  return messageRooms.map((item: MessageRoom) => {
    return item.customerID;
  });
};

const createProjectIds = (messageRooms: MessageRoom[]): string[] => {
  return messageRooms.map((item: MessageRoom) => {
    return item.projectID;
  });
};

const createMessageRoomPartialIds = (
  messageRooms: MessageRoom[]
): MessageRoomPartialID[] => {
  return messageRooms.map((item: MessageRoom) => {
    return new MessageRoomPartialID(item.projectID, item.customerID);
  });
};

const getAllMessageRooms = (): Promise<MessageRoom[]> => {
  return messageRoomRepository.getAllByClient(1, 1000);
};

const getAllByProjectIds = (projectIds: string[]): Promise<Project[]> => {
  return projectRepository.getAllByIDs(projectIds);
};

const getAllByCustomerIds = (customerIds: string[]): Promise<Customer[]> => {
  return customerRepository.getAllByIDs(customerIds);
};

const getAllNewestByRoomIDs = (
  messageRoomIds: MessageRoomPartialID[]
): Promise<Message[]> => {
  return messageRepository.getAllNewestByRooms(messageRoomIds);
};

const getContractAllNewestByIDs = (
  ids: TContractIdWithoutCompany[]
): Promise<Contract[]> => {
  return contractRepository.getMulti(ids);
};

const createIdsList = (
  messageRoomList: MessageRoom[]
): TContractIdWithoutCompany[] => {
  const resList: TContractIdWithoutCompany[] = [];
  messageRoomList.map((item: MessageRoom) => {
    const res: TContractIdWithoutCompany = {
      projectID: item.projectID,
      customerID: item.customerID,
    };
    resList.push(res);
  });

  return resList;
};

export { handlePollTaskWatcherInClientMessageRooms };



// WEBPACK FOOTER //
// ./src/presentation/handler/client_message_rooms_handler.ts